import { useStores } from "stores";
import { ENV } from "utils/hostEnv";
import { PROD } from "utils/config";
import { observer } from "mobx-react";
import React, { Suspense } from "react";
import { RESPONSE_OVERRIDE_ENABLED } from "api/devtools/responseOverrideLink";

const ExperimentsModal = React.lazy(() =>
  import("./ExperimentModal").then((module) => ({
    default: module.ExperimentsModal,
  })),
);

const ResponseOverridePanel = React.lazy(() =>
  import("api/devtools/ResponseOverridePanel").then((module) => ({
    default: module.ResponseOverridePanel,
  })),
);

const AppDevtools = React.lazy(() =>
  import("./AppDevtools").then((module) => ({
    default: module.AppDevtools,
  })),
);

export const InternalFeatures = observer(function InternalFeatures() {
  const {
    userStore: { currentEmail },
  } = useStores();

  // Test builds set this so docs screenshots and recordings show no dev-only
  // floating buttons.
  if (import.meta.env.VITE_HIDE_DEVTOOLS === "true") return null;

  const isInternal =
    currentEmail?.endsWith("@timescale.com") ||
    currentEmail?.endsWith("@tigerdata.com");

  // Experiments are available to internal users in prod too, for live
  // debugging; the response-override panel is dev-only since it could
  // confuse real customer flows.
  const showExperiments = ENV.NAME !== PROD || isInternal;
  const showOverrides = RESPONSE_OVERRIDE_ENABLED;
  // Framework devtools are for the developer's own machine only, so they use
  // the dev-server check rather than `ENV.NAME`: `ENVS.local` reports the same
  // name as the deployed dev environment.
  const showDevtools = import.meta.env.DEV;

  // Rendered next to the root route's outlet, which has no Suspense boundary:
  // without this one, loading these chunks would suspend the whole app into
  // the router's empty top-level fallback, blanking the boot shell.
  return (
    <Suspense fallback={null}>
      {showExperiments && <ExperimentsModal />}
      {showOverrides && <ResponseOverridePanel />}
      {showDevtools && <AppDevtools />}
    </Suspense>
  );
});
